import { useState, type FormEvent } from 'react';
import { Champ, Modale } from './Elements';
import { ChoixCreneau } from './ChoixCreneau';
import { ChoixSeance } from './ChoixSeance';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { enCentimes, enEuros, formaterDate, formaterMontant } from '../lib/format';
import type { Activite, Creneau, Inscription, SimulationChangementCreneau } from '../types';

/**
 * TRANSFERER UNE INSCRIPTION VERS UN AUTRE CRENEAU.
 *
 * Un seul geste pour quatre situations — et c'est le SERVEUR qui les
 * distingue, a partir de l'activite du creneau vise et de ce que l'eleve
 * a suivi. L'ecran ne fait que montrer les consequences avant qu'on
 * valide.
 *
 * MEME ACTIVITE, deux chemins (2026-11-04) :
 *   - l'eleve a deja suivi des cours sur l'ancien creneau : CHANGEMENT DE
 *     CRENEAU date. L'ancienne inscription s'arrete a son dernier cours,
 *     une nouvelle demarre au premier cours du nouveau creneau, et le du
 *     total ne bouge pas — il se partage entre les deux, les reglements
 *     suivent. C'est ce qui garde juste la paie de chaque professeur et
 *     les presences deja pointees ;
 *   - il n'y a jamais mis les pieds : simple deplacement d'horaire, rien
 *     de financier, sans alerte — signaler un danger qui n'existe pas
 *     apprend a ignorer les alertes qui comptent.
 *
 * ACTIVITE DIFFERENTE, deux chemins :
 *   - l'eleve a suivi l'ancien cours : arret au prorata puis reprise.
 *     Rien n'est detruit, un second transfert defait le premier ;
 *   - il ne l'a jamais suivi : erreur de saisie, l'ancienne inscription
 *     est supprimee. C'est le SEUL cas irreversible, et le seul qui
 *     porte l'encart rouge.
 */
export function FormulaireTransfert({
  inscription,
  creneaux,
  activites,
  onFermer,
  onEnregistre,
}: {
  inscription: Inscription | null;
  creneaux: Creneau[];
  /** Le tarif de reference vit sur l'activite, jamais sur le creneau. */
  activites: Activite[];
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [creneauId, setCreneauId] = useState('');
  // DEFAUT A « NON » quand l'activite change.
  //
  // Le transfert sert surtout a rattraper une saisie : l'eleve n'a jamais
  // mis les pieds dans l'ancien cours. C'est aussi le defaut le plus sur
  // a montrer — il ouvre l'encart rouge et passe le bouton en danger, si
  // bien que la branche destructrice ne peut pas etre empruntee sans
  // qu'on l'ait vue. L'inverse, un « Oui » discret, laisserait passer un
  // arret-reprise non voulu en silence.
  const [aSuivi, setASuivi] = useState(false);
  const [dateEffet, setDateEffet] = useState(new Date().toISOString().slice(0, 10));
  const [montantAncienne, setMontantAncienne] = useState('');
  const [montantNouvelle, setMontantNouvelle] = useState('');
  // DEFAUT A « OUI » quand l'activite ne change pas : en cours d'annee,
  // un eleve qui change d'horaire a presque toujours suivi des cours, et
  // le deplacement sec effacerait la paie des cours donnes. Ici aucune
  // branche n'est destructrice : le defaut est celui qui est juste le
  // plus souvent.
  const [memeActiviteSuivi, setMemeActiviteSuivi] = useState(true);
  const [dernierCours, setDernierCours] = useState('');
  const [premierCours, setPremierCours] = useState('');
  const [premierTouche, setPremierTouche] = useState(false);
  const [partAncienne, setPartAncienne] = useState('');
  const [partTouche, setPartTouche] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  // Les valeurs viennent de l'inscription DEJA CHARGEE par la liste,
  // jamais d'une requete propre au formulaire : c'est le defaut du
  // 2026-08-29, ou une case initialisee avant l'arrivee des donnees
  // supprimait une cotisation a l'enregistrement.
  if (inscription && inscription.id !== initialisePour) {
    setInitialisePour(inscription.id);
    setCreneauId('');
    setASuivi(false);
    setMontantAncienne(enEuros(inscription.montant_base));
    setMontantNouvelle('');
    setMemeActiviteSuivi(true);
    setDernierCours('');
    setPremierCours('');
    setPremierTouche(false);
    setPartAncienne('');
    setPartTouche(false);
    setErreur(null);
  }
  if (!inscription && initialisePour !== null) setInitialisePour(null);

  /**
   * CE QUI EST PROPOSE : les creneaux ou il reste de la place, sauf
   * celui qu'on quitte.
   *
   * Le plafond est celui de la SAISIE — capacite majoree de la tolerance
   * du type —, la meme regle que l'ecran d'inscription : entre capacite
   * et plafond un creneau reste proposé, marqué « en tolérance ». Un
   * transfert ne doit pas ouvrir une porte que la saisie ferme.
   *
   * Le creneau d'origine est retire : le proposer inviterait a un geste
   * sans effet, que le serveur refuse de toute facon.
   */
  const complet = (c: Creneau) =>
    c.capacite_avec_tolerance !== null && c.effectif >= c.capacite_avec_tolerance;
  const enTolerance = (c: Creneau) =>
    !complet(c) && c.capacite_max !== null && c.effectif >= c.capacite_max;
  const proposables = creneaux.filter((c) => !complet(c) && c.id !== inscription?.creneau_id);
  const masques = creneaux.length - proposables.length;

  const cible = creneaux.find((c) => c.id === creneauId) ?? null;
  const changeDActivite = Boolean(cible && cible.activite_id !== inscription?.activite_id);
  const changementDate = Boolean(cible) && !changeDActivite && memeActiviteSuivi;
  const tarifCible = cible
    ? (activites.find((a) => a.id === cible.activite_id)?.tarif ?? null)
    : null;

  // Le tarif de l'activite visee amorce le montant : c'est une reference
  // qu'on ajuste, jamais un montant impose (RG-02).
  if (changeDActivite && montantNouvelle === '' && tarifCible !== null) {
    setMontantNouvelle(enEuros(tarifCible));
  }

  // LE PARTAGE SE CALCULE SUR LE SERVEUR : il depend du calendrier des
  // deux jours, et c'est une regle d'argent. L'ecran redemande a chaque
  // dernier cours retenu.
  const simulation = useRessource<SimulationChangementCreneau | null>(
    () =>
      inscription && cible && changementDate
        ? api.get<SimulationChangementCreneau>(
            `/api/inscriptions/${inscription.id}/changement-creneau`,
            { creneauId: cible.id, dernierCours: dernierCours || undefined },
          )
        : Promise.resolve(null),
    [inscription?.id, cible?.id, changementDate, dernierCours],
  );
  const sim = changementDate ? simulation.donnees : null;

  // Les propositions du serveur remplissent ce que l'utilisateur n'a pas
  // touche. `useRessource` garde l'ancienne reponse pendant un
  // rechargement : on ne pose donc jamais une valeur « une fois », on la
  // compare a la proposition courante et on converge.
  if (sim && dernierCours === '' && sim.dernier_cours_propose) {
    setDernierCours(sim.dernier_cours_propose);
  }
  if (sim && !premierTouche && sim.premier_cours_propose && premierCours !== sim.premier_cours_propose) {
    setPremierCours(sim.premier_cours_propose);
  }
  if (sim && !partTouche && partAncienne !== enEuros(sim.part_ancienne)) {
    setPartAncienne(enEuros(sim.part_ancienne));
  }

  const partAncienneCentimes = enCentimes(partAncienne);
  const partNouvelleCentimes =
    sim && partAncienneCentimes !== null ? sim.montant_du - partAncienneCentimes : null;
  const datesAncien = sim
    ? sim.dates_ancien.filter((d) => !sim.date_debut || d >= sim.date_debut)
    : [];
  const datesNouveau = sim ? sim.dates_nouveau.filter((d) => d > dernierCours) : [];

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (!inscription || !cible) return;

    const corps: Record<string, unknown> = { creneauId };
    if (changeDActivite) {
      const nouvelle = enCentimes(montantNouvelle);
      if (nouvelle === null) {
        setErreur('Montant de la nouvelle inscription invalide : utilisez le format 390,00.');
        return;
      }
      corps.aSuivi = aSuivi;
      corps.dateEffet = dateEffet;
      corps.montantNouvelle = nouvelle;
      if (aSuivi) {
        const ancienne = enCentimes(montantAncienne);
        if (ancienne === null) {
          setErreur('Montant recalculé de l’ancienne inscription invalide.');
          return;
        }
        corps.montantAncienne = ancienne;
      }
    } else if (changementDate) {
      if (!dernierCours || !premierCours) {
        setErreur('Choisissez le dernier cours sur l’ancien créneau et le premier sur le nouveau.');
        return;
      }
      if (premierCours <= dernierCours) {
        setErreur('Le premier cours sur le nouveau créneau doit suivre le dernier cours sur l’ancien.');
        return;
      }
      if (partAncienneCentimes === null || !sim) {
        setErreur('Part de l’ancien créneau invalide : utilisez le format 192,00.');
        return;
      }
      if (partAncienneCentimes < 0 || partAncienneCentimes > sim.montant_du) {
        setErreur(`La part de l’ancien créneau doit être comprise entre 0 et ${formaterMontant(sim.montant_du)}.`);
        return;
      }
      corps.aSuivi = true;
      corps.dernierCours = dernierCours;
      corps.premierCours = premierCours;
      corps.montantAncienne = partAncienneCentimes;
    }

    setErreur(null);
    setEnCours(true);
    try {
      await api.post(`/api/inscriptions/${inscription.id}/transfert`, corps);
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Transfert impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre="Transférer vers un autre créneau"
      ouverte={Boolean(inscription)}
      onFermer={onFermer}
    >
      {inscription && (
        <form onSubmit={soumettre} className="space-y-4">
          <p className="text-sm text-encre-600">
            {inscription.personne_nom.toUpperCase()} {inscription.personne_prenom} —{' '}
            {inscription.activite_libelle}
            {inscription.montant_regle > 0 && (
              <>
                {' · déjà réglé '}
                <strong className="tabulaire">{formaterMontant(inscription.montant_regle)}</strong>
              </>
            )}
          </p>

          <Champ
            libelle="Nouveau créneau"
            aide={
              `Changer d’horaire dans la même activité ne change pas le montant dû ; ` +
              `changer d’activité le recalcule.` +
              (masques > 0 ? ` ${masques} créneau(x) complet(s) ne sont pas proposés.` : '')
            }
          >
            <ChoixCreneau
              creneaux={proposables}
              valeur={creneauId}
              onChanger={(id) => {
                setCreneauId(id);
                // Un autre creneau, un autre calendrier : les dates et le
                // partage se reproposent.
                setDernierCours('');
                setPremierCours('');
                setPremierTouche(false);
                setPartTouche(false);
              }}
              avecActivite
              sansOptionVide
              placeholder="Taper une activité, un jour, une heure, un professeur…"
              mention={(c) => (enTolerance(c) ? '· en tolérance' : '')}
            />
          </Champ>

          {cible && !changeDActivite && (
            <>
              <Champ libelle="L’élève a-t-il déjà suivi des cours sur l’ancien créneau ?">
                <select
                  className="champ"
                  value={memeActiviteSuivi ? 'oui' : 'non'}
                  onChange={(e) => setMemeActiviteSuivi(e.target.value === 'oui')}
                >
                  <option value="oui">Oui — changement de créneau à une date</option>
                  <option value="non">Non — simple déplacement d’horaire</option>
                </select>
              </Champ>

              {!memeActiviteSuivi && (
                <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-600">
                  Même activité : seul l’horaire change. Le montant dû et les règlements déjà
                  ventilés restent en place, et l’opération se refait dans l’autre sens.
                </p>
              )}

              {memeActiviteSuivi && sim && (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Champ
                      libelle="Dernier cours sur l’ancien créneau"
                      aide="Seules les dates de cours de ce créneau sont sélectionnables."
                    >
                      {datesAncien.length > 0 ? (
                        <ChoixSeance
                          dates={datesAncien}
                          valeur={dernierCours}
                          onChanger={(d) => {
                            setDernierCours(d);
                            // Changer de date repose les propositions.
                            setPremierTouche(false);
                            setPartTouche(false);
                          }}
                        />
                      ) : (
                        <p className="text-xs text-amber-700">
                          Aucun calendrier saisi pour ce jour : le partage reste à saisir à la main.
                        </p>
                      )}
                    </Champ>
                    <Champ
                      libelle="Premier cours sur le nouveau créneau"
                      aide="Proposé : le premier cours de la semaine suivante."
                    >
                      {datesNouveau.length > 0 ? (
                        <ChoixSeance
                          dates={datesNouveau}
                          valeur={premierCours}
                          onChanger={(d) => {
                            setPremierCours(d);
                            setPremierTouche(true);
                          }}
                        />
                      ) : (
                        <p className="text-xs text-amber-700">
                          Aucune date de cours après le dernier cours sur ce créneau.
                        </p>
                      )}
                    </Champ>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Champ
                      libelle="Part de l’ancien créneau (€)"
                      aide={
                        dernierCours
                          ? `${sim.seances_faites} séance(s) faite(s) sur ${sim.seances_possibles} · ` +
                            `proposé ${formaterMontant(sim.part_ancienne)}`
                          : 'Au prorata des séances faites.'
                      }
                    >
                      <input
                        className="champ tabulaire"
                        value={partAncienne}
                        onChange={(e) => {
                          setPartAncienne(e.target.value);
                          setPartTouche(true);
                        }}
                        inputMode="decimal"
                        required
                      />
                    </Champ>
                    <Champ
                      libelle="Part du nouveau créneau (€)"
                      aide={`Dû total inchangé : ${formaterMontant(sim.montant_du)}.`}
                    >
                      <input
                        className="champ tabulaire"
                        value={partNouvelleCentimes !== null ? enEuros(partNouvelleCentimes) : ''}
                        readOnly
                        tabIndex={-1}
                      />
                    </Champ>
                  </div>

                  <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-600">
                    Sans discontinuité de cours, le montant dû ne change pas : l’inscription
                    actuelle s’arrête
                    {dernierCours ? ` au ${formaterDate(dernierCours)}` : ''} avec sa part, une
                    nouvelle démarre
                    {premierCours ? ` le ${formaterDate(premierCours)}` : ''} avec le reste, et les
                    règlements déjà ventilés suivent. La réduction éventuelle est reprise telle
                    quelle. La fiche montrera deux lignes pour la même activité.
                  </p>
                </>
              )}
            </>
          )}

          {changeDActivite && (
            <>
              <Champ libelle="L’élève a-t-il déjà suivi l’ancien cours ?">
                <select
                  className="champ"
                  value={aSuivi ? 'oui' : 'non'}
                  onChange={(e) => setASuivi(e.target.value === 'oui')}
                >
                  <option value="oui">Oui — arrêter l’ancienne au prorata</option>
                  <option value="non">Non — erreur de saisie, la supprimer</option>
                </select>
              </Champ>

              <Champ
                libelle="Date d’effet"
                aide="Fin de l’ancienne inscription et début de la nouvelle."
              >
                <input
                  className="champ"
                  type="date"
                  value={dateEffet}
                  onChange={(e) => setDateEffet(e.target.value)}
                  required
                />
              </Champ>

              <div className="grid grid-cols-2 gap-3">
                {aSuivi && (
                  <Champ
                    libelle="Ancienne, recalculée (€)"
                    aide={`Initial : ${formaterMontant(inscription.montant_base)}. Le prorata est un arbitrage.`}
                  >
                    <input
                      className="champ tabulaire"
                      value={montantAncienne}
                      onChange={(e) => setMontantAncienne(e.target.value)}
                      inputMode="decimal"
                      required
                    />
                  </Champ>
                )}
                <Champ
                  libelle="Nouvelle inscription (€)"
                  aide={
                    tarifCible !== null
                      ? `Tarif de référence : ${formaterMontant(tarifCible)}.`
                      : 'Cette activité n’a pas de tarif de référence.'
                  }
                >
                  <input
                    className="champ tabulaire"
                    value={montantNouvelle}
                    onChange={(e) => setMontantNouvelle(e.target.value)}
                    inputMode="decimal"
                    required
                  />
                </Champ>
              </div>

              {aSuivi ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  L’ancienne inscription passera en fin anticipée au montant recalculé. Les
                  règlements suivront l’élève à hauteur de ce que coûte la nouvelle inscription ;{' '}
                  <strong>l’excédent éventuel restera à allouer</strong> sur son règlement et
                  apparaîtra en trop-perçu sur la fiche. Aucun règlement n’est supprimé, aucun
                  remboursement n’est engagé.
                </p>
              ) : (
                <p className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-800">
                  <strong>Cette opération est irréversible.</strong> L’ancienne inscription sera
                  supprimée : son historique, ses dates et son montant disparaîtront. Les règlements,
                  eux, ne sont pas supprimés — ils sont désalloués puis reventilés sur la nouvelle
                  inscription. Ne choisissez ceci que si l’élève n’a jamais suivi ce cours.
                </p>
              )}
            </>
          )}

          {erreur && <p className="text-sm text-red-700">{erreur}</p>}

          <div className="flex justify-end gap-2">
            <button type="button" className="bouton-secondaire" onClick={onFermer}>
              Annuler
            </button>
            <button
              type="submit"
              className={!changeDActivite || aSuivi ? 'bouton-principal' : 'bouton-danger'}
              disabled={enCours || !cible || (changementDate && !sim)}
            >
              {enCours ? 'Transfert…' : changementDate ? 'Changer de créneau' : 'Transférer'}
            </button>
          </div>
        </form>
      )}
    </Modale>
  );
}
